import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Button, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import useRequest from "@entrilia/shared/hooks/useRequest";
import biApi from "../../../../../../api/biApi";
import useClientContext from "../../../../../../context/useClientContext";
import { useReportsContext } from "../../contexts/reports/ReportsContext";
import AddGroupDialog from "../../groups/actions/AddGroupDialog.tsx";
import { useGroupsContext } from "../../groups/contexts/GroupsContext.ts";
import ReportsPageContainer from "../components/ReportsPageContainer";
import ReportsSideMenu from "../components/ReportsSideMenu.tsx";
import ReportsGridWrapper from "../grid/ReportsGridWrapper";
import useReportsWithSideMenu from "../hooks/useReportsWithSideMenu.ts";
import { getEditApiReportUrl } from "../utilities/editReportUrl.ts";

const CompanyApiReportsContainer = () => {
  usePageTitle("API Reports");
  const navigate = useNavigate();
  const { clientCode } = useClientContext();
  const { groups: reportGroups, actions: groupActions } = useGroupsContext();
  const { reports } = useReportsContext();

  const {
    filteredReports,
    groups,
    reportCountsByGroup,
    sideMenuFilter,
    sideMenuFilterType,
    selectedFilterLabel,
    setSideMenuFilter,
    toggleFavorite,
  } = useReportsWithSideMenu({
    reports,
    reportGroups,
    clientCode,
    getFavoriteReports: biApi.apiReports.getFavoriteReports,
    toggleFavoriteReport: biApi.apiReports.toggleFavoriteReport,
  });

  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);

  const { request: createDraftReport, isLoading: isCreatingDraftReport } = useRequest(
    biApi.apiReports.createDraftReport
  );

  const onCreateDraftReport = useCallback(async () => {
    const [report] = await createDraftReport();
    if (report) {
      navigate(getEditApiReportUrl(clientCode, report.reportId));
    }
  }, [createDraftReport, clientCode, navigate]);

  return (
    <Stack direction="row" sx={{ height: "100%", width: "100%" }}>
      <ReportsSideMenu
        title="API Reports"
        selectedFilter={sideMenuFilter}
        onFilterChange={setSideMenuFilter}
        groups={groups}
        reportCountsByGroup={reportCountsByGroup}
        groupType="report"
        onAddGroup={() => setShowAddGroupDialog(true)}
        onReorderGroups={(groups) => groupActions.reorder(clientCode, groups)}
      />
      <ReportsPageContainer
        title={selectedFilterLabel}
        Actions={
          <>
            <AuthorizedBox permissions={["ManageReports"]}>
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={onCreateDraftReport}
                disabled={isCreatingDraftReport}
                loading={isCreatingDraftReport}
              >
                Create New
              </Button>
            </AuthorizedBox>
          </>
        }
      >
        <ReportsGridWrapper
          reports={filteredReports}
          isGroupSelected={sideMenuFilterType === "group"}
          onToggleFavorite={toggleFavorite}
        />
      </ReportsPageContainer>
      {showAddGroupDialog && <AddGroupDialog clientCode={clientCode} onClose={() => setShowAddGroupDialog(false)} />}
    </Stack>
  );
};

export default CompanyApiReportsContainer;
